import React, { useState } from 'react';
import { Warehouse, Search } from 'lucide-react';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { InventoryService } from 'services/inventory/inventory.service';
import { Card } from 'components/common/Card';
import { Button } from 'components/common/Button';
import { Table, Column } from 'components/common/Table';
import { Badge } from 'components/common/Badge';
import { LoadingScreen } from 'components/common/Spinner';
import { InventoryItem, InventoryVarianceRow } from 'dtos/inventory/inventory.dto';
import { formatCurrency } from 'utils/formatters';
import { Pagination } from 'components/common/Pagination';

const PAGE_SIZE = 8;

const TABS = ['Current Inventory', 'Variance Report', 'Record Count'];

export default function InventoryPage() {
  const { org, currentStore } = useStore();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [countProduct, setCountProduct] = useState('');
  const [countQty, setCountQty] = useState('');
  const [countNotes, setCountNotes] = useState('');
  const [countSubmitted, setCountSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [variancePage, setVariancePage] = useState(1);

  const { data: inventory, loading: loadingInv } = useApi(
    () => InventoryService.list(org?.id ?? '', currentStore?.id ?? '', inventoryPage, PAGE_SIZE, search || undefined),
    [currentStore?.id, inventoryPage, search]
  );
  const { data: variances, loading: loadingVar } = useApi(
    () => InventoryService.getVariances(org?.id ?? '', currentStore?.id ?? '', variancePage, PAGE_SIZE),
    [currentStore?.id, variancePage]
  );

  const filtered = inventory?.data ?? [];

  const invColumns: Column<InventoryItem>[] = [
    { key: 'product', header: 'Product', render: i => (
      <div><p className="text-sm font-medium text-gray-900">{i.product_name}</p><p className="text-xs text-gray-400">{i.upc}</p></div>
    )},
    { key: 'category', header: 'Category', render: i => <span className="text-sm text-gray-600">{i.category ?? '—'}</span> },
    { key: 'type', header: 'Managed by', render: i => {
      const colors: Record<string, any> = { VENDOR_MANAGED: 'info', STORE_MANAGED: 'default' };
      return <Badge variant={colors[i.inventory_management_type] ?? 'default'}>{i.inventory_management_type.replace('_', ' ')}</Badge>;
    }},
    { key: 'qty', header: 'On Hand', align: 'right', sortable: true, render: i => <span className="text-sm font-medium">{i.quantity_on_hand}</span> },
    { key: 'cost', header: 'Unit Cost', align: 'right', render: i => <span className="text-sm font-mono">{formatCurrency(i.unit_cost, 2)}</span> },
    { key: 'value', header: 'Total Value', align: 'right', sortable: true, render: i => <span className="text-sm font-medium">{formatCurrency(i.total_value, 2)}</span> },
  ];

  const varColumns: Column<InventoryVarianceRow>[] = [
    { key: 'product', header: 'Product', render: v => <span className="text-sm font-medium text-gray-900">{v.product_name}</span> },
    { key: 'category', header: 'Category', render: v => <span className="text-sm text-gray-500">{v.category ?? '—'}</span> },
    { key: 'variance', header: 'Variance', align: 'right', render: v => (
      <span className={`text-sm font-semibold ${v.variance < 0 ? 'text-red-600' : 'text-green-600'}`}>
        {v.variance > 0 ? '+' : ''}{v.variance}
      </span>
    )},
    { key: 'impact', header: 'Est. Cost Impact', align: 'right', render: v => (
      <span className="text-sm font-medium text-orange-600">~{formatCurrency(v.estimated_variance_cost, 2)}</span>
    )},
  ];

  const handleSubmitCount = async () => {
    if (!countProduct || !countQty) return;
    setSubmitting(true);
    await InventoryService.submitCount(org?.id ?? '', currentStore?.id ?? '', countProduct, Number(countQty), countNotes);
    setSubmitting(false);
    setCountSubmitted(true);
    setCountProduct(''); setCountQty(''); setCountNotes('');
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Warehouse size={20} className="text-gray-600" />
        <h1 className="text-xl font-bold text-gray-900">Inventory</h1>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === i ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 0 && (
        <div className="space-y-3">
          <div className="relative max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search products…" value={search} onChange={e => { setSearch(e.target.value); setInventoryPage(1); }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <Card padding={false}>
            {loadingInv ? <LoadingScreen /> : (
              <>
                <Table columns={invColumns} data={filtered} keyExtractor={i => i.product_id} />
                {inventory && <Pagination page={inventoryPage} pageSize={PAGE_SIZE} total={inventory.meta.total} onPageChange={setInventoryPage} />}
              </>
            )}
          </Card>
        </div>
      )}

      {tab === 1 && (
        <Card padding={false}>
          {loadingVar ? <LoadingScreen /> : (
            <>
              <div className="p-4 bg-amber-50 border-b border-amber-100 text-sm text-amber-800">
                Variance may indicate shrinkage, receiving errors, or damage. Always investigate before conclusions.
              </div>
              <Table columns={varColumns} data={variances?.data ?? []} keyExtractor={v => v.product_id} />
              {variances && <Pagination page={variancePage} pageSize={PAGE_SIZE} total={variances.meta.total} onPageChange={setVariancePage} />}
              <div className="px-5 py-3 border-t bg-gray-50 text-xs text-gray-500">
                Total estimated impact: <strong className="text-orange-600">~{formatCurrency((variances?.data ?? []).reduce((s, v) => s + v.estimated_variance_cost, 0), 2)}</strong>
              </div>
            </>
          )}
        </Card>
      )}

      {tab === 2 && (
        <Card>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Record Physical Count</h3>
          {countSubmitted && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
              Count recorded successfully.
            </div>
          )}
          <div className="space-y-4 max-w-sm">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
              <select value={countProduct} onChange={e => setCountProduct(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Select product…</option>
                {(inventory?.data ?? []).map(i => <option key={i.product_id} value={i.product_id}>{i.product_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Physical Count</label>
              <input type="number" min="0" value={countQty} onChange={e => setCountQty(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              {countProduct && inventory && (
                <p className="text-xs text-gray-400 mt-1">
                  Expected: {inventory.data.find(i => i.product_id === countProduct)?.quantity_on_hand ?? '—'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea value={countNotes} onChange={e => setCountNotes(e.target.value)} rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <Button loading={submitting} onClick={handleSubmitCount} disabled={!countProduct || !countQty}>
              Submit Count
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

